import { useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { useAiConfig } from '@/hooks/use-ai'
import { useAiSources } from '@/hooks/use-ai-credits'
import type { FlowConfig } from '@/hooks/use-automation'

/** Kế hoạch AI trả về (cây bước) — dùng để hiển thị bản xem trước dạng chữ. */
export interface PlanRule { field: string; op: string; value?: string }
export type PlanStep =
  | { kind: 'action'; label?: string; actionType: string; config?: Record<string, unknown> }
  | { kind: 'delay'; label?: string; duration: number; unit: string }
  | { kind: 'condition'; label?: string; logic?: 'and' | 'or'; rules: PlanRule[]; yes?: PlanStep[]; no?: PlanStep[] }

export interface ComposePlan {
  trigger: { type: string; label?: string; onlyIf?: { logic?: 'and' | 'or'; rules: PlanRule[] } }
  steps: PlanStep[]
}

export interface ComposeResult {
  flowConfig: FlowConfig
  plan: ComposePlan
  name?: string
  summary: string
  questions: string[]
  warnings: string[]
}

export interface ComposeHistoryItem { role: 'user' | 'assistant'; text: string }

export function useComposeFlow() {
  return useMutation({
    mutationFn: async (body: { prompt: string; currentFlow?: FlowConfig | null; history?: ComposeHistoryItem[] }) =>
      (await api.post<ComposeResult>('/automation/ai/compose', body, { timeout: 120_000 })).data,
  })
}

export type AiReadiness =
  | { state: 'loading' }
  | { state: 'ready' }
  | { state: 'off' | 'no_source' | 'blocked'; reason: string }

/**
 * Tổ chức đã nối AI chưa? Dùng cùng nguồn dữ liệu màn Nguồn AI: nhà cung cấp đang chọn phải có
 * "AI của eCDP" khả dụng hoặc key riêng đã nhập. Không đọc được cấu hình (thiếu quyền xem AI,
 * API cũ) thì coi là sẵn sàng — backend vẫn chặn và báo lỗi rõ khi gọi.
 */
export function useAiReadiness(): AiReadiness & { refetch: () => void; fetching: boolean } {
  const cfg = useAiConfig()
  const src = useAiSources()
  const refetch = () => { void cfg.refetch(); void src.refetch() }
  const fetching = cfg.isFetching || src.isFetching
  if (cfg.isLoading || src.isLoading) return { state: 'loading', refetch, fetching }
  const c = cfg.data
  const s = src.data
  if (!c || !s) return { state: 'ready', refetch, fetching }
  if (!c.enabled) return { state: 'off', reason: i18n.t('autoCore:ai.off'), refetch, fetching }
  if (s.orgBlocked) return { state: 'blocked', reason: i18n.t('autoCore:ai.blocked'), refetch, fetching }
  const p = s.providers.find((x) => x.provider === c.provider)
  const ok = !!p && (p.source === 'platform'
    ? s.platformEnabled && p.platformAvailable
    : p.byok.configured && p.byok.status !== 'invalid')
  if (!ok) return { state: 'no_source', reason: i18n.t('autoCore:ai.noSource'), refetch, fetching }
  return { state: 'ready', refetch, fetching }
}
